import React, { useEffect, useRef, useState } from "react";
import { Redirect, generatePath, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Typography } from "@mui/material";

import Breadcrumbs from "../../../../../components/BreadCrumbs";
import Card from "../../../../../components/Card";
import LogoUpload from "../../../../../components/LogoUpload";
import GeneralInfoSection from "../../../../../components/Company/CompanyEditForm/GeneralInfoSection";
import ContactPersonSection from "../../../../../components/Company/CompanyEditForm/ContactPersonSection";
import CompanyEditDocuments from "../../../../../components/Company/CompanyEditDocuments";
import { buildCompanyPatchBody } from "../../../../../components/Company/CompanyEditForm/buildCompanyPatchBody";
import { UIButton } from "../../../../../components/ui";
import messages from "../../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";
import { isSuperAdmin } from "../../../../../utils/Permissions";
import { uploadToS3, directories } from "../../../../../utils/S3";
import { getCitiesRequest } from "../../../../../store/Lookups/actions";
import { updateCompanyRequest } from "../../../../../store/CompaniesList/actions";
import {
  clearCompanyDetails,
  getCompanyDetailsRequest,
} from "../../../../../store/RegistrationReq/actions";
import { normalizeDialCode, showErrorMsg } from "../../../../../utils/Helpers";

import "./CompanyEditPage.scss";

const getCompanyDetailsPath = (id) =>
  generatePath(ROUTE_PATHS.companiesListCompanyDetails, { id });

const getNationalPhoneNumber = (phoneNumber, dialCode) => {
  const digits = String(phoneNumber ?? "").replace(/\D/g, "");
  const dial = String(dialCode ?? "+966").replace(/\D/g, "");

  return dial && digits.startsWith(dial) ? digits.slice(dial.length) : digits;
};

const getOwnerPhoneNumber = (company) => {
  const owner = company?.company_owner;

  return (
    owner?.phone_number ||
    owner?.mobile_number ||
    owner?.phone ||
    owner?.mobile ||
    company?.phone_number ||
    company?.mobile_number ||
    ""
  );
};

const CompanyEditPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { registrationReq, companiesList: cl, shared, company: companyMsgs } =
    messages[lang];
  const updateLoading = useSelector(
    (state) => state.companiesList.updateLoading,
  );
  const companyDetails = useSelector(
    (state) => state.companiesRegistration.companyDetails,
  );
  const companyDetailsRequestId = useSelector(
    (state) => state.companiesRegistration.companyDetailsRequestId,
  );
 
  const companyDetailsError = useSelector(
    (state) => state.companiesRegistration.companyDetailsError,
  );
  const cities = useSelector((state) => state.lookups.cities);

  const company =
    companyDetails && String(companyDetailsRequestId) === String(id)
      ? companyDetails
      : null;

  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [pendingDocs, setPendingDocs] = useState({});
  const [uploadState, setUploadState] = useState({
    target: null,
    percent: 0,
  });
  const logoFileRef = useRef(logoFile);
  const pendingDocsRef = useRef(pendingDocs);
  const companyRef = useRef(company);
  logoFileRef.current = logoFile;
  pendingDocsRef.current = pendingDocs;
  companyRef.current = company;

  useEffect(() => {
    if (id) {
      dispatch(getCompanyDetailsRequest({ id, force: true }));
    }
    if (!cities.length) {
      dispatch(getCitiesRequest({ page: -1 }));
    }
    return () => dispatch(clearCompanyDetails());
  }, [dispatch, id, lang]);

  useEffect(() => {
    if (!company) return;
    setLogoPreview(company.image_url || null);
    setLogoFile(null);
    setPendingDocs({});
  }, [company?.id]);

  const navigateToDetails = () => {
    history.push(getCompanyDetailsPath(id));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: (() => {
      const owner = company?.company_owner;
      const ownerDialCode = owner?.country?.dial_code || "+966";

      return {
        name_en: company?.name_en || "",
        name_ar: company?.name_ar || "",
        city_id: company?.city?.id ?? null,
        owner_name: owner?.name || "",
        owner_phone_number: getNationalPhoneNumber(
          getOwnerPhoneNumber(company),
          ownerDialCode,
        ),
        owner_dial_code: ownerDialCode,
        owner_country_short: (owner?.country?.short_code || "SA").toLowerCase(),
      };
    })(),
    validationSchema: Yup.object({
      name_en: Yup.string().required("enNameRequired"),
      city_id: Yup.mixed().required("cityRequired"),
      owner_name: Yup.string().required("nameRequired"),
      owner_phone_number: Yup.string().required("generalFieldRequired"),
    }),
    onSubmit: async (values) => {
      try {
        const logo = logoFileRef.current;
        const pendingDocsNow = pendingDocsRef.current;

        let image_url;
        if (logo) {
          const logoExt = logo.name.split(".").pop();
          const logoName = `logo.${logoExt}`;
          setUploadState({ target: "logo", percent: 0 });
          const logoUploaded = await uploadToS3(
            logo,
            logoName,
            directories.companyRegistration,
            {
              onProgress: (percent) =>
                setUploadState({ target: "logo", percent }),
            },
          );
          image_url = logoUploaded?.link ?? null;
          if (!image_url) {
            showErrorMsg(cl.logoUploadFailed);
            return;
          }
        }

        let commercial_record_url;
        const pendingCr = pendingDocsNow.commercial_record_url;
        if (pendingCr !== undefined) {
          if (pendingCr === null) {
            commercial_record_url = null;
          } else if (pendingCr instanceof File) {
            const crExt = pendingCr.name.split(".").pop() || "pdf";
            const crName = `cr.${crExt}`;
            setUploadState({ target: "commercial_record_url", percent: 0 });
            const crUploaded = await uploadToS3(
              pendingCr,
              crName,
              directories.companyRegistration,
              {
                onProgress: (percent) =>
                  setUploadState({ target: "commercial_record_url", percent }),
              },
            );
            commercial_record_url = crUploaded?.link ?? null;
            if (!commercial_record_url) {
              showErrorMsg(cl.commercialRecordUploadFailed);
              return;
            }
          }
        }

        const c = companyRef.current;
        const ownerId = c?.company_owner?.id;
        const dialNormalized = normalizeDialCode(values.owner_dial_code);
        const payload = {
          name_en: values.name_en,
          name_ar: values.name_ar,
          city_id: values.city_id,
          company_owner_attributes: {
            ...(ownerId != null ? { id: ownerId } : {}),
            name: values.owner_name,
            dial_code: dialNormalized,
            phone_number: String(values.owner_phone_number || "").replace(
              /\s+/g,
              "",
            ),
          },
        };

        if (image_url) {
          payload.image_url = image_url;
        }

        if (commercial_record_url !== undefined) {
          payload.commercial_record_url = commercial_record_url;
          const crNumber = c?.commercial_record;
          if (
            crNumber != null &&
            String(crNumber).trim() !== "" &&
            commercial_record_url !== null
          ) {
            payload.commercial_record = String(crNumber).trim();
          }
        }

        let delegation_document_url;
        const pendingDelegation = pendingDocsNow.delegation_document_url;
        if (pendingDelegation !== undefined) {
          if (pendingDelegation === null) {
            delegation_document_url = null;
          } else if (pendingDelegation instanceof File) {
            const ext = pendingDelegation.name.split(".").pop() || "pdf";
            const fileName = `delegation.${ext}`;
            setUploadState({ target: "delegation_document_url", percent: 0 });
            const uploaded = await uploadToS3(
              pendingDelegation,
              fileName,
              directories.companyRegistration,
              {
                onProgress: (percent) =>
                  setUploadState({
                    target: "delegation_document_url",
                    percent,
                  }),
              },
            );
            delegation_document_url = uploaded?.link ?? null;
            if (!delegation_document_url) {
              showErrorMsg(cl.delegationDocumentUploadFailed);
              return;
            }
          }
        }

        let personal_id_url;
        const pendingIdDoc = pendingDocsNow.personal_id_url;
        if (pendingIdDoc !== undefined) {
          if (pendingIdDoc === null) {
            personal_id_url = null;
          } else if (pendingIdDoc instanceof File) {
            const ext = pendingIdDoc.name.split(".").pop() || "pdf";
            const fileName = `id_document.${ext}`;
            setUploadState({ target: "personal_id_url", percent: 0 });
            const uploaded = await uploadToS3(
              pendingIdDoc,
              fileName,
              directories.companyRegistration,
              {
                onProgress: (percent) =>
                  setUploadState({ target: "personal_id_url", percent }),
              },
            );
            personal_id_url = uploaded?.link ?? null;
            if (!personal_id_url) {
              showErrorMsg(cl.idDocumentUploadFailed);
              return;
            }
          }
        }

        if (
          delegation_document_url !== undefined ||
          personal_id_url !== undefined
        ) {
          payload.company_owner_attributes = {
            ...payload.company_owner_attributes,
            ...(delegation_document_url !== undefined
              ? {
                  delegation_document_url:
                    delegation_document_url === null
                      ? ""
                      : delegation_document_url,
                }
              : {}),
            ...(personal_id_url !== undefined
              ? {
                  personal_id_url: personal_id_url === null ? "" : personal_id_url,
                }
              : {}),
          };
        }

        dispatch(
          updateCompanyRequest({
            id,
            data: buildCompanyPatchBody(payload),
            onSuccess: navigateToDetails,
          }),
        );
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
    },
  });

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const formIsLoading = formik.isSubmitting || updateLoading;
  const uploadProgressLabel =
    uploadState.target != null
      ? `${companyMsgs.uploadFile.uploading} ${uploadState.percent}%`
      : undefined;

  const breadcrumbCompanyName = company?.name_en || cl.companyProfile;

  const renderContent = () => {
    if (companyDetailsError) {
      return (
        <Box className="company-edit-page__empty-state">
          <Typography color="error">
            {companyDetailsError || registrationReq.companyDetailsLoadError}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        component="form"
        className="company-edit-page__form company-edit-details"
        onSubmit={formik.handleSubmit}
      >
        <div className="company-edit-page__grid">
          <div className="company-edit-page__main">
            <Card
              title={registrationReq.companyInformation}
              classNames={{
                root: "company-edit-page__card",
                body: "company-edit-page__card-body",
              }}
            >
              <div className="company-edit-page__company-card-content">
                <LogoUpload
                  label={companyMsgs.logo.shortLabel}
                  required
                  size={133}
                  borderRadius="8px"
                  imagePreview={logoPreview}
                  onFileChange={(file, preview) => {
                    setLogoFile(file);
                    setLogoPreview(preview);
                  }}
                  disabled={formIsLoading}
                  isUploading={uploadState.target === "logo"}
                  uploadProgress={
                    uploadState.target === "logo"
                      ? uploadState.percent
                      : undefined
                  }
                  uploadProgressLabel={
                    uploadState.target === "logo"
                      ? uploadProgressLabel
                      : undefined
                  }
                  changeLabel={cl.logoUploadChange}
                  hintLabel={cl.logoUploadHint}
                />
                <GeneralInfoSection
                  values={formik.values}
                  setFieldValue={formik.setFieldValue}
                  setFieldTouched={formik.setFieldTouched}
                  cities={cities}
                  errors={formik.errors}
                  touched={formik.touched}
                  commercialRecord={company?.commercial_record || ""}
                  disabled={formIsLoading}
                  hideTitle
                  classNames={{
                    base: "company-edit-page__section",
                    content: "company-edit-page__section-content",
                    inputGroupName: "company-edit-page__name-grid",
                  }}
                />
              </div>
            </Card>

            <Card
              title={cl.contactPerson}
              classNames={{
                root: "company-edit-page__card",
                body: "company-edit-page__card-body",
              }}
            >
              <ContactPersonSection
                values={formik.values}
                setFieldValue={formik.setFieldValue}
                setFieldTouched={formik.setFieldTouched}
                errors={formik.errors}
                touched={formik.touched}
                ownerEmail={company?.company_owner?.email || ""}
                disabled={formIsLoading}
                hideTitle
                classNames={{
                  base: "company-edit-page__section",
                  inputGroupName: "company-edit-page__contact-grid",
                }}
              />
            </Card>
          </div>

          <CompanyEditDocuments
            commercialRecordUrl={company?.commercial_record_url}
            delegationDocumentUrl={company?.company_owner?.delegation_document_url}
            idDocumentUrl={company?.company_owner?.personal_id_url}
            pendingDocs={pendingDocs}
            onDocumentChange={(key, fileOrNull) =>
              setPendingDocs((prev) => ({ ...prev, [key]: fileOrNull }))
            }
            uploadState={uploadState}
            uploadProgressLabel={uploadProgressLabel}
            disabled={formIsLoading}
            classNames={{ root: "company-edit-page__documents" }}
          />
        </div>
      </Box>
    );
  };

  return (
    <div className="company-edit-page">
      <div className="mt-3">
        <Breadcrumbs
          list={[
            { path: ROUTE_PATHS.companiesList, label: cl.listBreadcrumb },
            { path: getCompanyDetailsPath(id), label: breadcrumbCompanyName },
            { path: "", label: cl.editCompanyProfile },
          ]}
          BreadCrumbHasBackIcon={false}
          className="mb-3"
        />
      </div>

      {renderContent()}

      <div className="company-edit-page__action-bar">
        <UIButton
          type="button"
          variant="outlined"
          size="medium"
          disabled={formIsLoading}
          onClick={() => history.push(ROUTE_PATHS.companiesList)}
        >
          {shared.cancel}
        </UIButton>
        <UIButton
          type="submit"
          variant="contained"
          size="medium"
          disabled={formIsLoading || !company}
          loading={formIsLoading}
          onClick={formik.submitForm}
        >
          {cl.saveChanges}
        </UIButton>
      </div>
    </div>
  );
};

export default CompanyEditPage;
